import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ResizeObserver } from '@juggle/resize-observer';
import * as d3 from 'd3';

import { getMaxValuePercentageColor } from '@app/models/circulo-encoder-calibration';

// The axis reaches this much further than the permissible phase error, so that the line marking the error sits at
// the same height whichever magnetic ring the encoder reads.
const axisHeadroomFactor = 1.2;

// The axis bound to use while no calibration has reported its permissible phase error yet.
const defaultAxisBound = 300;

export interface EncoderCalibrationIteration {
  name: string;
  color: string;
  series: number[][];
}
@Component({
  selector: 'app-circulo-encoder-calibration-plot',
  template: '',
})
export class CirculoEncoderCalibrationPlotComponent implements OnChanges, OnInit, OnDestroy {
  private elementRef = inject(ElementRef);

  @Input() data: EncoderCalibrationIteration[] = [];
  @Input() xDomain = [0, 360];
  @Input() maxPermissiblePhaseError: number = null;
  @Input() maxValuePercentageIndex: number;

  @Output() maxValuePercentageMouseenter = new EventEmitter<any>();
  @Output() maxValuePercentageMouseleave = new EventEmitter<any>();

  width: number;
  height: number;

  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  plot: d3.Selection<SVGGElement, unknown, null, undefined>;
  maxValue: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;

  resizeObserver: ResizeObserver;

  get maxValueDataPoint() {
    return this.data?.[this.data.length - 1]?.series[this.maxValuePercentageIndex];
  }

  ngOnInit(): void {
    const pEl = this.elementRef.nativeElement.parentElement;

    this.resizeObserver = new ResizeObserver(() => {
      this.svg?.remove();
      this.create();
      this.update();
    });

    this.resizeObserver.observe(pEl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxPermissiblePhaseError && !changes.maxPermissiblePhaseError.firstChange) {
      this.svg?.remove();
      this.create();
      this.update();
    } else if (changes.data && !changes.data.firstChange) {
      this.update();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  create(): void {
    const rect = this.elementRef.nativeElement.parentElement.getBoundingClientRect();
    const { width: pWidth, height: pHeight } = rect;

    const margin = { top: 24, right: 16, bottom: 44, left: 52 };
    this.width = pWidth - margin.left - margin.right;
    this.height = pHeight - margin.top - margin.bottom;

    this.svg = d3
      .select(this.elementRef.nativeElement)
      .append('svg')
      .attr('viewBox', `0 0 ${pWidth} ${pHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin')
      .attr('width', this.width + margin.left + margin.right)
      .attr('height', this.height + margin.top + margin.bottom);

    this.g = this.svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    this.xScale = d3.scaleLinear().domain(this.xDomain).range([0, this.width]);

    this.g.append('g').attr('transform', `translate(0,${this.height})`).call(d3.axisBottom(this.xScale));

    const axisBound = this.maxPermissiblePhaseError
      ? this.maxPermissiblePhaseError * axisHeadroomFactor
      : defaultAxisBound;

    this.yScale = d3.scaleLinear().domain([-axisBound, axisBound]).range([this.height, 0]);

    this.g.append('g').call(d3.axisLeft(this.yScale));

    if (this.maxPermissiblePhaseError) {
      [this.maxPermissiblePhaseError, -this.maxPermissiblePhaseError].forEach((value) => {
        this.g
          .append('line')
          .attr('stroke', '#fb262e')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4')
          .attr('x1', 0)
          .attr('y1', () => this.yScale(value))
          .attr('x2', this.width)
          .attr('y2', () => this.yScale(value));
      });
    }

    this.g
      .append('line')
      .attr('stroke', '#6ca856')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '8 4')
      .attr('x1', 0)
      .attr('y1', () => this.yScale(0))
      .attr('x2', this.width)
      .attr('y2', () => this.yScale(0));

    this.g
      .append('text')
      .text('Phase error [inc]')
      .attr('font-size', '0.9rem')
      .attr('font-family', 'Roboto, sans-serif')
      .attr('fill', 'currentColor')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - this.height / 2)
      .attr('dy', '1em')
      .attr('text-anchor', 'middle');

    this.g
      .append('text')
      .text('Mechanical angle [deg]')
      .attr('font-size', '0.9rem')
      .attr('font-family', 'Roboto, sans-serif')
      .attr('fill', 'currentColor')
      .attr('y', this.height + margin.top)
      .attr('x', this.width / 2)
      .attr('dy', '1em')
      .attr('text-anchor', 'middle');
  }

  update(): void {
    this.plot?.remove();

    this.plot = this.g.append('g');

    this.data?.forEach((data) => {
      const { color } = data;

      this.plot
        .append('g')
        .selectAll('circle')
        .data(data.series)
        .enter()
        .append('circle')
        .attr('cx', (d) => this.xScale(d[0]))
        .attr('cy', (d) => this.yScale(d[1]))
        .attr('r', 1.5)
        .attr('fill', color);
    });

    this.data?.forEach((data, index) => {
      const { color, name } = data;

      this.plot
        .append('text')
        .text(name)
        .attr('dx', 10)
        .attr('dy', index * 18 + 12 - 16)
        .attr('fill', color)
        .attr('font-family', 'Roboto, sans-serif')
        .attr('font-size', '1rem');
    });

    if (this.maxPermissiblePhaseError) {
      this.plot
        .append('text')
        .text('Max permissible error')
        .attr('dx', this.width)
        .attr('dy', this.yScale(this.maxPermissiblePhaseError) - 10)
        .attr('fill', '#fb262e')
        .attr('font-family', 'Roboto, sans-serif')
        .attr('font-size', '1rem')
        .attr('text-anchor', 'end');
    }

    this.maxValue?.remove();

    const maxValue = this.maxValueDataPoint?.[1];

    if (maxValue && this.maxPermissiblePhaseError) {
      const limit = this.maxPermissiblePhaseError;

      let maxValuePercentage = 100 - Math.floor((Math.abs(maxValue) / limit) * 100);

      if (maxValuePercentage < 0) {
        maxValuePercentage = 0;
      }

      let yScaleValue = this.maxValueDataPoint[1];
      if (maxValue < -limit) {
        yScaleValue = -limit;
      } else if (maxValue > limit) {
        yScaleValue = limit;
      }

      const maxValueX = this.xScale(this.maxValueDataPoint[0]);
      const maxValueY = this.yScale(yScaleValue);

      const errorLineY1 = maxValue > 0 ? this.yScale(limit) : this.yScale(-limit);

      const gY = maxValueY - (maxValueY - errorLineY1) / 2;

      const stroke = getMaxValuePercentageColor(maxValuePercentage);

      this.maxValue = this.g.append('g');

      this.maxValue
        .append('line')
        .attr('stroke', '#25343b')
        .attr('stroke-width', 1)
        .attr('x1', maxValueX)
        .attr('y1', () => errorLineY1)
        .attr('x2', maxValueX)
        .attr('y2', () => maxValueY);

      const cg = this.maxValue
        .append('g')
        .attr('transform', `translate(${maxValueX},${gY})`)
        .on('mouseenter', (event) => this.maxValuePercentageMouseenter.emit({ event, percentage: maxValuePercentage }))
        .on('mouseleave', (event) => this.maxValuePercentageMouseleave.emit({ event, percentage: maxValuePercentage }));

      cg.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', 16)
        .attr('stroke', '#25343b')
        .attr('stroke-width', 1.25)
        .attr('fill', stroke);

      cg.append('text')
        .text(`${maxValuePercentage}%`)
        .attr('fill', 'white')
        .attr('font-family', 'Roboto, sans-serif')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('dy', '4px')
        .attr('text-anchor', 'middle')
        .style('pointer-events', 'none');
    }
  }
}
